import { useTimeZoneContext } from '@ais-ui/utils';
import {
  ReportBackendTypes,
  useCompleteReportFetchingProcess,
  useGetExtraJobData,
} from '@ais-ui/websockets';
import { useMapPanelContext } from '../Map/MapPanel/MapPanelContext';
import { useMapReportOverlay } from './useMapReportOverlay';
import { useEffect, useRef } from 'react';
import { CENTERCOMPONENTS } from '../Map/MapPanel/CenterComponents';
import { useReportContext } from './ReportContext';
import { toast } from '@ais-ui/ui-core';

// Report types that don't have table data
const REPORTS_WITHOUT_TABLE = [
  ReportBackendTypes.DENSITY,
  ReportBackendTypes.CSV_DATA_EXPORT,
  ReportBackendTypes.CSV_DATA_EXPORT_FAST,
];

// Report types that have no visual output at all (no table, no overlay, no legend)
const REPORTS_WITHOUT_VISUAL_OUTPUT = [
  ReportBackendTypes.CSV_DATA_EXPORT,
  ReportBackendTypes.CSV_DATA_EXPORT_FAST,
];

/**
 * Check if a report type has visual output (table, overlay, or legend)
 */
export const hasVisualOutput = (reportType: string): boolean => {
  return !REPORTS_WITHOUT_VISUAL_OUTPUT.includes(reportType as ReportBackendTypes);
};

/**
 * Hook to manage report processing - coordinates fetching report data,
 * ship data, and updating the map panel.
 */
export const useManageReportProcessing = (jobId: string, zoom: boolean) => {
  const { timeZone } = useTimeZoneContext();
  const { reportType } = useReportContext();
  const hasShownTimeoutToast = useRef(false);
  const previousJobId = useRef<string | null>(null);

  // Reset toast flag when jobId changes
  useEffect(() => {
    if (previousJobId.current !== jobId) {
      hasShownTimeoutToast.current = false;
      previousJobId.current = jobId;
    }
  }, [jobId]);

  const {
    data: reportResult,
    isLoading: reportLoading,
    isError: reportError,
    status: reportStatus,
  } = useCompleteReportFetchingProcess(jobId, timeZone);

  const {
    data: shipData,
    isLoading: shipDataLoading,
    isError: shipDataError,
  } = useGetExtraJobData(jobId, timeZone, -360, 360, -360, 360);

  // Show error toast when report loading times out
  useEffect(() => {
    if (reportStatus === 'TimedOut' && !hasShownTimeoutToast.current) {
      hasShownTimeoutToast.current = true;
      toast({
        title: 'Report loading failed',
        description: 'The report took too long to load. Please try again.',
        variant: 'destructive',
      });
    }
  }, [reportStatus]);

  const {
    setCenterComponent,
    setCenterComponentData,
    setLoadingCenterComponentData,
    setTableExpanded,
  } = useMapPanelContext();

  useMapReportOverlay(jobId, zoom ? reportResult?.imageExtent : undefined);

  // Update map panel state based on loading/data state
  useEffect(() => {
    const isLoading = shipDataLoading || reportLoading;
    setLoadingCenterComponentData(isLoading);

    // Skip showing table for report types that don't have table data
    if (reportType && REPORTS_WITHOUT_TABLE.includes(reportType as ReportBackendTypes)) {
      // Clear the table for reports without table data
      setCenterComponent(CENTERCOMPONENTS.NONE);
      setCenterComponentData(null);
      return;
    }

    if (shipData !== undefined) {
      setCenterComponentData(shipData);
      setCenterComponent(CENTERCOMPONENTS.SHIPTABLE);
      // Ensure table is expanded when showing a new report
      setTableExpanded(true);
    }
    
    // Fall back to reportResult.shipData if shipData is empty
    if (reportResult?.shipData !== undefined && shipData?.length === 0) {
      setCenterComponentData(reportResult.shipData);
      setCenterComponent(CENTERCOMPONENTS.SHIPTABLE);
      // Ensure table is expanded when showing a new report
      setTableExpanded(true);
    }
  }, [
    shipData,
    reportResult,
    shipDataLoading,
    reportLoading,
    shipDataError,
    reportError,
    setCenterComponent,
    setCenterComponentData,
    setLoadingCenterComponentData,
    setTableExpanded,
    reportType,
  ]);

  return reportResult;
};
